import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Navbar } from './components/layout/Navbar';
import { Hero } from './components/home/Hero';
import { ServerStats } from './components/home/ServerStats';
import { Features } from './components/home/Features';
import { Toaster } from 'react-hot-toast';
import { NotFound } from './components/ui/NotFound';
import { PageTransition } from './components/ui/PageTransition';
import { AnimatePresence } from 'framer-motion';
import './i18n/config';
import Leaderboard from './pages/Leaderboard';
import { Vote } from './pages/Vote';
import { VoteRedirect } from './pages/VoteRedirect';
import { Store } from './pages/Store';
import { ProductDetail } from './pages/ProductDetail';
import { Checkout } from './pages/Checkout';
import { useSmoothScroll } from './hooks/useSmoothScroll';
import { usePageTitle } from './hooks/usePageTitle';
import { Footer } from './components/layout/Footer';
import { SEO } from './components/shared/SEO';
import { AdminLogin } from './pages/admin/Login';
import { AdminStore } from './pages/admin/Store';
import { AdminHome } from './pages/admin/Home';
import { AdminImages } from './pages/admin/Images';
import { ProtectedRoute } from './components/admin/ProtectedRoute';
import { SharedElementProvider } from './contexts/SharedElementContext';

const HomePage = () => {
  const { t } = useTranslation();
  return (
    <PageTransition>
      <SEO 
        title={t('seo.home.title')}
        description={t('seo.home.description')}
      />
      <Hero />
      <ServerStats />
      <Features />
      <div className="mt-20">
        <Footer />
      </div>
    </PageTransition>
  );
};

function LanguageRedirect() {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Get preferred language from localStorage or browser
  const preferredLanguage = localStorage.getItem('i18nextLng') || navigator.language.split('-')[0];
  const targetLang = ['en', 'id'].includes(preferredLanguage) ? preferredLanguage : 'en';
  
  // Only change language if it's different
  if (i18n.language !== targetLang) {
    i18n.changeLanguage(targetLang);
  }

  // If we're already at a language route, don't redirect
  if (location.pathname.match(/^\/(en|id)/)) {
    return null;
  }

  // Remove any trailing slashes for consistency
  const cleanPath = location.pathname.replace(/\/$/, '');
  return <Navigate to={`/${targetLang}${cleanPath}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  usePageTitle();
  
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Root and non-localized paths */}
        <Route path="/" element={<LanguageRedirect />} />
        <Route path="/leaderboard" element={<LanguageRedirect />} />
        <Route path="/vote" element={<LanguageRedirect />} />
        <Route path="/vote/:index" element={<VoteRedirect />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin/home"
          element={
            <ProtectedRoute>
              <AdminHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/store"
          element={
            <ProtectedRoute>
              <AdminStore />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/images"
          element={
            <ProtectedRoute>
              <AdminImages />
            </ProtectedRoute>
          }
        />
        
        {/* English routes */}
        <Route path="/en" element={<HomePage />} />
        <Route path="/en/leaderboard" element={
          <PageTransition>
            <Leaderboard />
          </PageTransition>
        } />
        <Route path="/en/vote" element={
          <PageTransition>
            <Vote />
          </PageTransition>
        } />
        <Route path="/en/vote/:index" element={<VoteRedirect />} />
        <Route path="/en/store" element={
          <PageTransition>
            <Store />
          </PageTransition>
        } />
        <Route path="/en/store/product/:id" element={
          <PageTransition>
            <ProductDetail />
          </PageTransition>
        } />
        <Route path="/en/store/checkout/:reference" element={
          <PageTransition>
            <Checkout />
          </PageTransition>
        } />
        <Route path="/en/admin/login" element={<AdminLogin />} />
        <Route
          path="/en/admin/home"
          element={
            <ProtectedRoute>
              <AdminHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/en/admin/store"
          element={
            <ProtectedRoute>
              <AdminStore />
            </ProtectedRoute>
          }
        />
        <Route
          path="/en/admin/images"
          element={
            <ProtectedRoute>
              <AdminImages />
            </ProtectedRoute>
          }
        />
        
        {/* Indonesian routes */}
        <Route path="/id" element={<HomePage />} />
        <Route path="/id/leaderboard" element={
          <PageTransition>
            <Leaderboard />
          </PageTransition>
        } />
        <Route path="/id/vote" element={
          <PageTransition>
            <Vote />
          </PageTransition>
        } />
        <Route path="/id/vote/:index" element={<VoteRedirect />} />
        <Route path="/id/store" element={
          <PageTransition>
            <Store />
          </PageTransition>
        } />
        <Route path="/id/store/product/:id" element={
          <PageTransition>
            <ProductDetail />
          </PageTransition>
        } />
        <Route path="/id/store/checkout/:reference" element={
          <PageTransition>
            <Checkout />
          </PageTransition>
        } />
        <Route path="/id/admin/login" element={<AdminLogin />} />
        <Route
          path="/id/admin/home"
          element={
            <ProtectedRoute>
              <AdminHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/id/admin/store"
          element={
            <ProtectedRoute>
              <AdminStore />
            </ProtectedRoute>
          }
        />
        <Route
          path="/id/admin/images"
          element={
            <ProtectedRoute>
              <AdminImages />
            </ProtectedRoute>
          }
        />

        {/* Catch non-localized paths and redirect */}
        <Route path="/leaderboard" element={<LanguageRedirect />} />
        <Route path="/vote" element={<LanguageRedirect />} />
        <Route path="/store" element={<LanguageRedirect />} />
        <Route path="/store/product/:id" element={<LanguageRedirect />} />
        
        {/* 404 pages for each language */}
        <Route path="/en/*" element={
          <PageTransition>
            <NotFound />
          </PageTransition>
        } />
        <Route path="/id/*" element={
          <PageTransition>
            <NotFound />
          </PageTransition>
        } />
        
        {/* Catch all other paths */}
        <Route path="*" element={<LanguageRedirect />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  useSmoothScroll();

  return (
    <Router>
      <SharedElementProvider>
        <div className="min-h-screen">
          <Navbar />
          <AppRoutes />

          <Toaster 
            position="bottom-center"
            toastOptions={{
              duration: 1500,
              style: {
                background: 'rgba(31, 41, 55, 0.8)',
                color: '#fff',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '8px 16px',
                fontSize: '0.875rem',
                maxWidth: '350px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
              },
              success: {
                iconTheme: {
                  primary: '#f8cb08',
                  secondary: 'rgba(31, 41, 55, 0.8)',
                },
                style: {
                  backgroundColor: 'rgba(31, 41, 55, 0.8)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: 'rgba(31, 41, 55, 0.8)',
                },
                style: {
                  backgroundColor: 'rgba(31, 41, 55, 0.8)',
                },
              },
            }}
          />
        </div>
      </SharedElementProvider>
    </Router>
  );
}

export default App;